import React, { useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { CircularProgress } from '@mui/material';
import { CellContext, type ColumnDef } from '@tanstack/react-table';
import useOffers from 'api/useOffers';
import usePricing from 'api/usePricing';
import type {
  Offer,
  ProductsSequence,
  TimeFrameObject
} from 'common/contracts';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { DATE_TIME_FORMAT_SHORTER } from 'constants/constants';
import { EOfferStatus, OfferType, SortingOffersValue } from 'constants/enums';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { usePageTypeByFeatureFlag } from 'hooks/usePageTypeByFF';
import { usePaginationFromURL } from 'hooks/usePaginationFromURL';
import { useTableSorting } from 'hooks/useTableSorting';
import { getConvertedPrice } from 'utils/getConvertedPrice';
import { permissionsUtil } from 'utils/permissionsUtil';
import { statusMapping, statusUtils } from 'utils/scheduleUtils';
import { statusFilterOptions } from 'utils/selectOptionsUtils';
import { EToasterStatus, showToast } from 'utils/showToast';

import Button from 'components/Button/Button';
import Chip from 'components/Chip/Chip';
import FilterDropdownCard from 'components/Filter/FilterDropdownCard';
import FilterSearchCard from 'components/Filter/FilterSearchCard';

import ActionCell from '../../components/DataTableComponent/components/ActionCell';
import DataTable from '../../components/DataTableComponent/DataTable';
import PortalHeader from '../../components/PortalHeader/PortalHeader';
import SortableHeader from '../../components/SortableHeader/SortableHeader';
const dayjsUTC = dayjs.extend(utc);

type RowData = {
  publisherOfferId: string;
  offerId: string;
  productsSequence: ProductsSequence[];
  createdAt: string;
  expirationDate: string;
  status: string;
  active: boolean;
  schedule?: {
    permanent: boolean;
    interval?: string;
    timeFrames?: TimeFrameObject[];
  };
  deeplinkUrl: string;
};

const CheckoutLinksTable = () => {
  const navigate = useNavigate();
  const { publisherId } = useParams();
  const location = useLocation();
  const { page, rows } = usePaginationFromURL('offers');
  const [currentPage, setCurrentPage] = useState(page);
  const { sorting, updateSorting } = useTableSorting(
    SortingOffersValue.CREATED_AT
  );

  const { getOffers, deleteOffer, toggleOfferStatus } = useOffers({
    offerId: undefined,
    offerType: OfferType.CHECKOUT_LINK,
    customPagination: { currentPage, rowsPerPage: rows },
    sorting
  });
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [skuFilterValue, setSkuFilterValue] = useState<string>('');
  const [statusFilterValue, setStatusFilterValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const { getPricings } = usePricing();
  const { isPageDisabled } = usePageTypeByFeatureFlag('Checkout Links');

  const isEmpty = getOffers.data?.offers && getOffers.data.offers.length === 0;
  const totalCount = getOffers.data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / rows);

  useEffect(() => {
    setIsLoading(
      getOffers.isFetching ||
        getOffers.isRefetching ||
        getOffers.isLoading ||
        getPricings.isRefetching
    );
  }, [
    getOffers.data?.offers,
    getOffers.isRefetching,
    getPricings.isRefetching
  ]);

  useEffect(() => {
    if (!selectedOffer) return;
    deleteOffer.mutate(selectedOffer, {
      onSuccess: () => {
        showToast({
          message: 'Checkout link deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        getOffers.refetch();
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete checkout link',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
      },
      onSettled: () => {
        setSelectedOffer(null);
      }
    });
  }, [selectedOffer]);

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set('page', String(currentPage));
    queryParams.set('rows', String(rows));
    queryParams.set('sortValue', sorting.sortValue);
    queryParams.set('direction', sorting.direction);

    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true
    });
    getOffers.refetch();
  }, [currentPage, rows, sorting]);

  useEffect(() => {
    getPricings.refetch();
  }, []);

  const isActionEnabled = useMemo(
    () =>
      permissionsUtil.isActionEnabled(
        null,
        EPermissionGroup.OFFERS,
        EPermissionAction.MANAGE,
        publisherId
      ),
    [publisherId]
  );

  const canUpdateOfferStatus = useMemo(
    () =>
      permissionsUtil.isActionEnabled(
        null,
        EPermissionGroup.OFFERS,
        EPermissionAction.UPDATE_OFFER_STATUS,
        publisherId
      ),
    [publisherId]
  );

  const filteredData =
    getOffers?.data?.offers.filter((offer: Offer) => {
      const isSkuMatch = offer.publisherOfferId
        .toLowerCase()
        .includes(skuFilterValue.toLowerCase());

      const offerStatus = statusUtils.getOfferStatus(offer, offer.active);

      return (
        (!statusFilterValue ||
          statusFilterValue === 'All' ||
          statusFilterValue === offerStatus ||
          (statusFilterValue === EOfferStatus.INACTIVE &&
            offerStatus === EOfferStatus.INACTIVE)) &&
        isSkuMatch
      );
    }) || [];

  const columns: ColumnDef<RowData, unknown>[] = [
    {
      accessorKey: 'deeplinkUrl',
      header: 'Links',
      cell: (cell: CellContext<RowData, unknown>) => {
        const { deeplinkUrl } = cell.row.original;

        return (
          <Button
            onClick={() => handleGetCheckoutLink(deeplinkUrl)}
            className="text-gray-600 hover:text-black w-15"
            variant="ghost"
          >
            <FontAwesomeIcon icon="fa-solid fa-link" />
          </Button>
        );
      }
    },
    {
      accessorKey: 'publisherOfferId',
      header: 'External ID',
      cell: (cell: CellContext<RowData, unknown>) => cell.getValue()
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: (cell: CellContext<RowData, unknown>) => cell.getValue()
    },
    {
      accessorKey: 'price',
      header: 'Price',
      cell: (cell: CellContext<RowData, unknown>) => {
        if (getPricings.isLoading) return '';
        const productsSequence = cell.row.original
          .productsSequence as ProductsSequence[];
        return getConvertedPrice(
          {
            row: { productsSequence: productsSequence || [] }
          },
          getPricings.data.result
        );
      }
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <SortableHeader
          label="Created at"
          sortValue={SortingOffersValue.CREATED_AT}
          currentSortValue={sorting.sortValue}
          currentDirection={sorting.direction}
          onSort={updateSorting}
        />
      ),
      cell: (cell: CellContext<RowData, unknown>) =>
        dayjsUTC
          .utc(cell.getValue() as boolean)
          .format(DATE_TIME_FORMAT_SHORTER)
    },
    {
      accessorKey: 'expirationDate',
      header: 'Expiration date',
      cell: (cell: CellContext<RowData, unknown>) => {
        const createdAt = cell.row.original.createdAt;
        if (!createdAt) return <div className="text-black">-</div>;

        const expirationDate = new Date(createdAt);
        expirationDate.setMonth(expirationDate.getMonth() + 6);

        return dayjs(expirationDate).format(DATE_TIME_FORMAT_SHORTER);
      }
    },
    {
      accessorKey: 'active',
      header: 'Status',
      cell: (cell: CellContext<RowData, unknown>) => {
        const isActive = cell.getValue() as boolean;
        const status = statusUtils.getOfferStatus(cell.row.original, isActive);

        return (
          <Chip variant="flat" status={statusMapping[status]}>
            {status}
          </Chip>
        );
      }
    },
    {
      accessorKey: 'actions',
      header: '',
      maxSize: 60,
      minSize: 60,
      size: 60,
      cell: (cell: CellContext<RowData, unknown>) => {
        const { original } = cell.row;
        return (
          <div className="max-w-[60px] w-[60px]">
            <ActionCell
              rowData={original}
              actions={[
                {
                  label: original.active ? 'Deactivate' : 'Activate',
                  handler: () =>
                    handleToggleActiveStatus(original.offerId, original.active),
                  disabled: !canUpdateOfferStatus
                },
                {
                  label: 'Edit',
                  handler: () => navigate(`./form/${original.offerId}`),
                  disabled: !isActionEnabled
                },
                {
                  label: 'Duplicate',
                  handler: () => navigate(`./dup/${original.offerId}`),
                  disabled: !isActionEnabled
                },
                {
                  label: 'Delete',
                  handler: () => {
                    setSelectedOffer(original.offerId);
                  },
                  disabled: !isActionEnabled
                }
              ]}
            />
          </div>
        );
      }
    }
  ];

  const handleGetCheckoutLink = (deeplinkUrl: string) => {
    const tempInput = document.createElement('input');
    tempInput.setAttribute('readonly', ''); // Prevent virtual keyboard on iOS
    tempInput.value = deeplinkUrl;
    tempInput.style.position = 'absolute';
    tempInput.style.left = '-9999px'; // Move off-screen
    document.body.appendChild(tempInput);

    try {
      if (navigator.userAgent.match(/ipad|iphone/i)) {
        // iOS specific handling
        tempInput.contentEditable = 'true';
        tempInput.readOnly = false;

        // Select the text
        const range = document.createRange();
        range.selectNodeContents(tempInput);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        tempInput.setSelectionRange(0, 999999);
      } else {
        // All other browsers
        tempInput.select();
      }

      document.execCommand('copy');
      showToast({
        message: 'Checkout link copied to clipboard',
        status: EToasterStatus.SUCCESS
      });
    } catch (err) {
      console.error('Copy failed:', err);
      showToast({
        message: 'Please copy manually: ' + deeplinkUrl,
        status: EToasterStatus.ERROR
      });
    } finally {
      document.body.removeChild(tempInput);
    }
  };

  const handleToggleActiveStatus = async (offerId: string, active: boolean) => {
    toggleOfferStatus.mutate(
      { active: !active, offerId },
      {
        onSuccess: () => {
          showToast({
            message: active
              ? 'Checkout link deactivated successfully'
              : 'Checkout link activated successfully',
            status: EToasterStatus.SUCCESS
          });
          getOffers.refetch();
        },
        onError: () => {
          showToast({
            message: 'Error editing checkout link',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  };

  return (
    <div
      className={`${isPageDisabled ? 'opacity-50 pointer-events-none' : 'opacity-100 pointer-events-auto'}`}
    >
      <PortalHeader
        title="Checkout Links"
        primaryButtonText="Create New Link"
        onPrimaryButtonClick={() => navigate('./form')}
        isCreateButton
      />
      {!isEmpty && (
        <div className="flex px-9 gap-3">
          <FilterSearchCard
            header="External ID"
            filterValue={skuFilterValue}
            setFilterValue={setSkuFilterValue}
            isLabelSearchBy
          />
          <FilterDropdownCard
            header="Status"
            filterValue={statusFilterValue}
            setFilterValue={setStatusFilterValue}
            options={statusFilterOptions}
          />
        </div>
      )}
      {isLoading ? (
        <CircularProgress className="absolute top-1/2 left-1/2 h-16 w-16" />
      ) : (
        <div className="px-9 pb-9 pt-2.5">
          <DataTable
            data={filteredData}
            columns={columns}
            rowIdIdentifier="offerId"
            rowCount={totalCount}
            rowsPerPage={rows}
            showPagination={totalPages > 1}
            manualPagination
            pageIndex={currentPage}
            pageCount={totalPages}
            onPageChange={setCurrentPage}
            emptyStateConfig={{
              title: 'Checkout link',
              isEmpty,
              description: (
                <>
                  Checkout links simplify purchases: one tap, auto-login, <br />
                  preselected offers and amounts.
                </>
              ),
              onClick: () => navigate('./form')
            }}
            bordered
          />
        </div>
      )}
    </div>
  );
};

export default CheckoutLinksTable;
